import { Popover, Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { OutlineCircleQuestionIcon } from '@frontend/components';

import { isMobileBrowser } from 'utils/browserUtils';

import { viewerInteractions } from '../data/actions';
import { getAllPartFiles } from '../data/partFileCadData/reducer';

const { Option } = Select;
const Container = styled.div`
  align-items: center;
  display: flex;
  flex: 0;
  margin: 0 10px 10px;
`;

const ControlsInfoWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const ControlsHeader = styled.span`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const PartviewerActionsHeader = styled.span`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  margin-top: 10px;
`;

const ControlsContent = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  width: 350px;
`;

const ControlsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
`;

const ControlTitle = styled.span`
  flex-basis: 50%;
  font-weight: 600;
  margin-right: 5px;
`;

const ControlValue = styled.span`
  flex-basis: 50%;
`;

const StyledSelect = styled(Select)`
  flex-grow: 1;
  margin-right: 10px;

  .ant-select-selection-placeholder {
    color: #444;
  }
`;

const ControlsInfo = () => {
  const isMobile = isMobileBrowser();
  const controlsContent = (
    <ControlsInfoWrapper>
      <ControlsHeader>Assembly Tree Controls</ControlsHeader>
      <ControlsContent>
        <ControlsRow>
          <ControlTitle>Focus:</ControlTitle>
          {isMobile ? (
            <ControlValue>Double Tap/Long Press Menu</ControlValue>
          ) : (
            <ControlValue>Double Click/Right Click Menu</ControlValue>
          )}
        </ControlsRow>
        {!isMobile && (
          <ControlsRow>
            <ControlTitle>View Thumbnail:</ControlTitle>
            <ControlValue>Hover Over Icon</ControlValue>
          </ControlsRow>
        )}
      </ControlsContent>
      <PartviewerActionsHeader>
        Partviewer Actions (CAD Only)
      </PartviewerActionsHeader>
      <ControlsContent>
        <ControlsRow>
          <ControlTitle>Toggle Visibility:</ControlTitle>
          <ControlValue>Checkbox</ControlValue>
        </ControlsRow>
        <ControlsRow>
          <ControlTitle>Toggle Select:</ControlTitle>
          {isMobile ? (
            <ControlValue>Single Tap</ControlValue>
          ) : (
            <ControlValue>Single Click</ControlValue>
          )}
        </ControlsRow>
        <ControlsRow>
          <ControlTitle>Zoom to Fit:</ControlTitle>
          {isMobile ? (
            <ControlValue>Long Press Menu</ControlValue>
          ) : (
            <ControlValue>Right Click Menu</ControlValue>
          )}
        </ControlsRow>
        <ControlsRow>
          <ControlTitle>Isolate:</ControlTitle>
          {isMobile ? (
            <ControlValue>Long Press Menu</ControlValue>
          ) : (
            <ControlValue>Right Click Menu</ControlValue>
          )}
        </ControlsRow>
        <ControlsRow>
          <ControlTitle>Show All Bodies:</ControlTitle>
          {isMobile ? (
            <ControlValue>Long Press Menu</ControlValue>
          ) : (
            <ControlValue>Right Click Menu</ControlValue>
          )}
        </ControlsRow>
      </ControlsContent>
    </ControlsInfoWrapper>
  );

  return (
    <Popover
      placement="rightTop"
      content={controlsContent}
      trigger={['hover', 'click']}
    >
      <OutlineCircleQuestionIcon size={2.5} />
    </Popover>
  );
};

export const CadTreeSearch = () => {
  const dispatch = useDispatch();

  const allPartFiles = useSelector(getAllPartFiles);
  const [searchData, setSearchData] = useState([]);
  const [searchKey, setSearchKey] = useState(null);

  useEffect(() => {
    // map part data to search values
    const mappedData = allPartFiles.map(({ filename, uuid }) => ({
      label: filename,
      value: uuid,
    }));
    const sortedData = mappedData.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
    setSearchData(sortedData);
  }, [allPartFiles]);

  const searchFilter = (inputValue, option) => {
    return (
      option.props.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
    );
  };

  const handleSearchSelect = useCallback(
    (value) => {
      dispatch(viewerInteractions.focusOnPartFile({ partFileUuid: value }));
      setSearchKey(null);
    },
    [dispatch],
  );

  return (
    <Container>
      <StyledSelect
        filterOption={searchFilter}
        onSelect={handleSearchSelect}
        placeholder="Search for Component In Tree"
        showArrow={false}
        showSearch={true}
        value={searchKey}
      >
        {searchData.map(({ label, value }) => (
          <Option key={value} value={value} label={label}>
            {label}
          </Option>
        ))}
      </StyledSelect>
      <ControlsInfo />
    </Container>
  );
};
